import { FILTER_BY_KEY, MISSING, NO_EXPIRY, EXPIRED, TAB_LABEL, type TabKey } from './filters'

/**
 * A view is a saved position of the agent list: which tab, which dropdowns,
 * what is in the search box.
 *
 * Three quite different things produce one — Pearl offering a group, the
 * What's missing screen, and Renee saving one she has set up by hand — and all
 * three go through `describeView` and the roster's `applyView`. A view is
 * therefore always something she could have reached with the dropdowns
 * herself, which is what makes it safe to let Pearl build one.
 */
export type View = {
  label: string
  filters: { key: string; value: string }[]
  tab: TabKey
  search: string
  include_departed: boolean
  sort: string | null
}

export type SavedView = View & { id: string; saved_at: string }

export const emptyView = (label: string): View => ({
  label,
  filters: [],
  tab: 'all',
  search: '',
  include_departed: false,
  sort: null,
})

export const TAB_PATH: Record<TabKey, string> = {
  all: '/',
  capped: '/capped',
  elite: '/elite',
  leaders: '/leaders',
}

/**
 * What a view will do, in the words the dropdowns use.
 *
 * `TAB_LABEL` used to be a private copy here that said "Elite" where
 * `filters.ts`'s copy (and everywhere else that names a tab — the nav, the
 * composer, `describeScheduledAudience`) says "Elite (12 mo)" — so a saved
 * view or a Pearl-built one describing itself would silently disagree with
 * the tab it actually opens. Reads the one shared copy instead (2026-08-22).
 */
export function describeView(v: View): string {
  const parts = v.filters.map((f) => {
    const def = FILTER_BY_KEY.get(f.key)
    const value = def?.display ? def.display(f.value) : f.value
    return `${def?.label ?? f.key}: ${value}`
  })
  if (v.search) parts.push(`search "${v.search}"`)
  if (v.include_departed) parts.push('including departed')
  if (v.tab !== 'all') parts.push(`${TAB_LABEL[v.tab]} tab`)
  return parts.join(' · ')
}

/**
 * The gaps in the roster, as views.
 *
 * Every one of these was invisible until now: you could only find the agents
 * with no address by knowing to set two dropdowns. The counts are worked out
 * from the live roster rather than written here, because a worklist that does
 * not go down as she works it is not a worklist.
 */
export const GAP_VIEWS: { view: View; note: string }[] = [
  {
    view: {
      ...emptyView('Licence already expired'),
      filters: [{ key: 'license_window', value: EXPIRED }],
      sort: 'license_expires',
    },
    note: 'Either they renewed and reZEN has not caught up, or the licence has lapsed. Worth knowing which.',
  },
  {
    view: {
      ...emptyView('Licence expiring within 90 days'),
      filters: [{ key: 'license_window', value: 'Within 90 days' }],
      sort: 'license_expires',
    },
    note: 'The next renewals coming at you.',
  },
  {
    view: {
      ...emptyView('No address at all'),
      filters: [
        { key: 'mailing_address', value: MISSING },
        { key: 'home_address', value: MISSING },
      ],
    },
    note: 'Nothing to post to. Export this list and ask them.',
  },
  {
    view: { ...emptyView('No mailing address'), filters: [{ key: 'mailing_address', value: MISSING }] },
    note: 'Their home address is used for labels instead, where there is one.',
  },
  {
    view: { ...emptyView('No home address'), filters: [{ key: 'home_address', value: MISSING }] },
    note: '',
  },
  {
    view: { ...emptyView('Address worth checking'), filters: [{ key: 'address_check', value: 'Worth checking' }] },
    note: 'The record holds more than one thing that looks like a zip — usually a new address pasted in front of an old one.',
  },
  {
    view: { ...emptyView('No licence expiry date'), filters: [{ key: 'license_expires', value: NO_EXPIRY }] },
    note: 'They will never appear in a renewal cohort until this is filled in.',
  },
  {
    view: { ...emptyView('No region'), filters: [{ key: 'region', value: 'Unassigned' }] },
    note: 'They fall out of every region filter, so a regional email would miss them. Usually somebody added by hand who never got classified.',
  },
  {
    view: { ...emptyView('No photo'), filters: [{ key: 'photo', value: MISSING }] },
    note: 'They show their initials in the list instead.',
  },
  {
    view: { ...emptyView('No birthday'), filters: [{ key: 'birthday_month', value: 'Not known yet' }] },
    note: 'Birthdays arrive from Metabase a few at a time, so this goes down by itself.',
  },
  {
    view: {
      ...emptyView('No work anniversary'),
      filters: [{ key: 'anniversary_month', value: 'Not known yet' }],
    },
    note: '',
  },
]

// ------------------------------------------------------------- saved views

const KEY = 'agent-care.saved-views'

/**
 * Saved views live in this browser, not in the database.
 *
 * That is a deliberate first cut: no migration to apply, no new write path,
 * and nothing that can go wrong for 785 agents. The cost is honest and small —
 * they are Renee's, on Renee's machine, and the Guide says so. Moving them to
 * a table later changes these four functions and nothing else.
 */
export function loadSavedViews(): SavedView[] {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Anything malformed is dropped rather than thrown: a corrupt entry must
    // not take the Views menu down with it.
    // Every field the rest of the app will read without checking. A view
    // missing `search` reaches `search.trim()` and takes the whole menu down
    // with it — on every reload, because the bad row is still in storage.
    return parsed.filter((v): v is SavedView => {
      if (!v || typeof v !== 'object') return false
      const s = v as SavedView
      return (
        typeof s.id === 'string' &&
        typeof s.label === 'string' &&
        typeof s.search === 'string' &&
        typeof s.tab === 'string' &&
        typeof s.include_departed === 'boolean' &&
        Array.isArray(s.filters) &&
        s.filters.every((f) => f && typeof f.key === 'string' && typeof f.value === 'string')
      )
    })
  } catch {
    return []
  }
}

/**
 * Returns whether the write actually landed.
 *
 * Swallowing the failure and returning the list anyway looked fine and was a
 * lie: the menu reloads from storage every time it opens, so a "saved" view
 * vanished the next time she looked, with nothing having said no.
 */
function write(views: SavedView[]): { views: SavedView[]; stored: boolean } {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(views))
    return { views, stored: true }
  } catch {
    return { views, stored: false }
  }
}

export function saveView(view: View): { views: SavedView[]; stored: boolean } {
  const now = new Date()
  const saved: SavedView = {
    ...view,
    id: `${now.getTime()}-${Math.round(Math.random() * 1e6)}`,
    saved_at: now.toISOString(),
  }
  // Replacing by name rather than piling up near-identical entries: saving
  // "2027 renewals" twice should leave one.
  const rest = loadSavedViews().filter((v) => v.label.toLowerCase() !== view.label.toLowerCase())
  return write([...rest, saved].sort((a, b) => a.label.localeCompare(b.label)))
}

export function deleteView(id: string): { views: SavedView[]; stored: boolean } {
  return write(loadSavedViews().filter((v) => v.id !== id))
}
